import axios, { AxiosResponse } from 'axios';
import { CryptoUtilities } from 'securedrive-crypto';
import {
  FileInfo,
  GrantItem,
  InboxContent,
  VirtualFileSystemProp,
} from '../..';
import { setShares } from '../../state';
import API_CALL from '../API';

export type ShareStatus = 'OK' | 'ENCRYPTED_MODIFIED' | 'DELETED';

export interface ShareItem extends FileInfo {
  id: string;
  pathname: string;
  expires_on: number;
  created_on: number;
  download_count: number;
  file_status: ShareStatus;
  expired: boolean;
  url?: string;
  vfs: VirtualFileSystemProp;
}

export interface ShareResponse {
  id: string;
  url: string;
}

export type Shares = Record<VirtualFileSystemProp, ShareItem[]>;

export interface Inbox {
  grants: GrantItem[];
  contents: InboxContent[];
}

export interface ShareEntryResponse extends FileInfo {
  download_url: string;
  shared_enc_dek?: string;
  file: string;
}
const getShares = async (): Promise<Shares> => {
  const allShares = await getListPublicShare();
  const shares: Shares = {
    SecurePassword: [],
    SecureInBox: [],
    SecureVault: [],
    SecureSync: [],
  };
  for (const share of allShares) {
    shares[share.vfs].push(share);
  }

  setShares(shares);
  return shares;
};

const getListPublicShare = async (
  folder?: VirtualFileSystemProp
): Promise<ShareItem[]> => {
  return await API_CALL({
    url: '/share/public',
    method: 'GET',
  }).then((response: ShareItem[]) => {
    let shares: ShareItem[] = response;

    shares = shares.map((share) => ({
      ...share,
      type: 'share',
      vfs: share.pathname.split('/')[0] as VirtualFileSystemProp,
      url: getShareUrl(share.id),
      key: share.id,
      label: share.pathname,
    }));
    if (folder) {
      shares = shares.filter((share) => share.pathname.startsWith(folder));
    }
    return shares;
  });
};

const postPublicShare = async (
  pathname: string,
  shared_enc_dek: string | undefined,
  duration: number | 1800 // 30 minutes
): Promise<ShareResponse> => {
  return await API_CALL({
    url: '/share/public',
    method: 'POST',
    data: { pathname, shared_enc_dek, duration },
  })
    .then((response: ShareResponse) => {
      const id = response.id;
      return {
        id,
        url: getShareUrl(id),
      };
    })
    .catch((error) => {
      throw error;
    });
};

/**
 * Get the public share entry
 * @param share_id - The ID of the share
 * @returns The share entry
 */
const getPublicShareEntry = async (
  share_id: string
): Promise<ShareEntryResponse> => {
  return await API_CALL({
    url: `/share/public/${share_id}`,
    method: 'GET',
  });
};

const sendPublicShareViaEmail = async (
  share_id: string,
  recipients: string[],
  message: string
): Promise<void> => {
  return await API_CALL({
    url: `/share/public/${share_id}/send`,
    method: 'POST',
    data: { recipients, message },
  });
};

const updatePublicShare = async (
  id: string,
  shared_enc_dek?: string | null,
  duration?: number
): Promise<ShareResponse> => {
  return await API_CALL({
    url: `/share/public/${id}`,
    method: 'PUT',
    data: { shared_enc_dek, duration },
  });
};

const deletePublicShare = async (id: string): Promise<void> => {
  return await API_CALL({
    url: `/share/public/${id}`,
    method: 'DELETE',
  });
};

const getSmartshareGrant = async (grantId: string): Promise<GrantItem> => {
  return await API_CALL({
    url: `/smartshare/grant/${grantId}`,
    method: 'GET',
    // Configurazione headers necessari
    headers: {
      accept: 'application/json, text/plain, */*',
      'cache-control': 'no-cache',
      pragma: 'no-cache',
    },
    // Includiamo le credenziali per i cookies
    credentials: 'include',
  }).catch((error: string) => {
    if (error.includes('412')) {
      throw new Error('EXPIRED');
    } else if (error.includes('404')) {
      throw new Error('NOT_FOUND');
    } else {
      throw error;
    }
  });
};

const getShareUrl = (share_id: string): string => {
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH
    ? `/${process.env.NEXT_PUBLIC_BASE_PATH}`
    : '';
  //https://securedrivecl.infocert.it/app/#/smartshare/306477dfc9059ccd5144dc1a4211c39ec4d27963892f8df2681f1aad1021d3bed0965d2c8929ecbb9f7a5418ee8c2a0886c5c1649c0366e153a4d88022700261
  return `${window.location.origin}${basePath}/#/public/${share_id}`;
};

// Interfaccia per i parametri di accesso
interface SmartshareAccessParams {
  content_length: number;
  content_md5: string;
  content_type: string;
  filename: string;
}

interface GrantAccess
  extends Pick<
    GrantItem,
    | 'id'
    | 'user_id'
    | 'name'
    | 'recipient'
    | 'kind'
    | 'created_on'
    | 'expires_on'
    | 'sent_on'
    | 'message'
    | 'usage_count'
  > {
  requested_on: number;
  access_id: string;
  upload_url: string;
}

// Funzione per richiedere l'accesso allo smartshare
const getSmartshareAccess = async (
  grantId: string,
  params: SmartshareAccessParams
): Promise<GrantAccess> => {
  return await API_CALL({
    url: `/smartshare/grant/${grantId}/access`,
    method: 'POST',
    headers: {
      'content-type': 'application/json;charset=UTF-8',
      'cache-control': 'no-cache',
      pragma: 'no-cache',
    },
    data: params,
    credentials: 'include',
  });
};

const performPublicUpload = async (
  uploadUrl: string,
  file: File,
  contentMD5: string,
  signal?: AbortSignal,
  onProgressUpdate?: (progress: number) => void
): Promise<AxiosResponse> => {
  const contentMd5 = await CryptoUtilities.fromHexToBase64(contentMD5);

  return axios.put(uploadUrl, file, {
    headers: {
      accept: '*/*',
      'content-md5': contentMd5,
      'content-type': file.type,
      'cache-control': 'no-cache',
      pragma: 'no-cache',
      // Aggiungiamo headers per gestire CORS e sicurezza
      'sec-fetch-dest': 'empty',
      'sec-fetch-mode': 'cors',
      'sec-fetch-site': 'cross-site',
    },
    onUploadProgress: (progressEvent) => {
      const percentCompleted = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total!
      );
      if (onProgressUpdate) {
        onProgressUpdate(percentCompleted);
      }
    },
    signal,
    // Aggiungiamo configurazione per gestire le credenziali
    withCredentials: false,
  });
};

// Interfaccia per l'aggiornamento dell'accesso
interface UpdateSmartshareAccessParams extends SmartshareAccessParams {}

// Funzione per aggiornare l'accesso allo smartshare
const updateSmartshareAccess = async (
  grantId: string,
  accessId: string,
  params: UpdateSmartshareAccessParams
): Promise<void> => {
  return await API_CALL({
    url: `/smartshare/grant/${grantId}/access/${accessId}`,
    method: 'PUT',
    headers: {
      'content-type': 'application/json;charset=UTF-8',
      'cache-control': 'no-cache',
      pragma: 'no-cache',
    },
    data: params,
    credentials: 'include',
  });
};

export const ShareService = {
  getListPublicShare,
  postPublicShare,
  getPublicShareEntry,
  sendPublicShareViaEmail,
  updatePublicShare,
  deletePublicShare,
  getSmartshareGrant,
  getSmartshareAccess,
  performPublicUpload,
  updateSmartshareAccess,
  getShareUrl,
  getShares,
};
